// @ts-nocheck
/*
 * Port of tts/add-tts.js (1249 lines). BYTE-IDENTICAL.
 * TTS creator with two operator modes chosen by the user's ACL
 * `MODULE_ACL.TTS.tts_operator` (SARVAM | OMNI_VOICE). OMNI_VOICE (a.k.a.
 * EXPRESS INTERNAL) requires a saved voice-profile and supports a batch
 * "freeze N captions → generate all → save all" flow via
 * API_TTS_MULTI_LANGUAGE (which we just added to service/url.ts).
 * SARVAM is single-language single-caption.
 * The conversion service itself is a separate URL (`TTS_CONVERSION_URL`)
 * — v1 calls `fetch()` directly, we do the same to keep response shape
 * handling byte-identical.
 * Language-scoped input validation is done via `LANGUAGE_PATTERNS`
 * (hi-IN, eng-IN, pi-IN). Demo accounts see a live daily quota banner and
 * hard-block on `remaining <= 0`. Shows a "batch freeze" workflow with a
 * caption viewer/editor modal, plus single- and multi-language listen
 * modals before Save. `SsmlTextEditor` powers the script field.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
import { getApiCall } from '@/service/getApiCall';
import { postApiCall } from '@/service/postApiCall';
import { DEFAULT_TTS_OPERATOR, normalizeTtsOperatorForSave } from '@/utilities/ttsAcl';
import {
  API_TTS_CHANGE_STATUS,
  API_TTS_MULTI_LANGUAGE,
  API_VOICE_PROFILE,
  API_TTS_DEMO_QUOTA,
} from '@/service/url';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getUser } from '@/utilities/setting';
import { useNavigate } from 'react-router-dom';
import {
  ROUTES,
  FLASH_MESSAGE_ACTION_OBJECT,
  MODULE_ACL,
  USER_ROLES,
  getTtsConversionUrl,
} from '@/utilities/constant';
import { AclDenied } from '@/components/feedback/AclDenied';
import { fetchAndSetFlashMessage } from '@/service/fetchFlashMessage';
import {
  FaMicrophone,
  FaGlobe,
  FaVenusMars,
  FaMale,
  FaFemale,
  FaFolder,
  FaFileAlt,
  FaPaperPlane,
  FaHeadphones,
  FaSave,
  FaTrashAlt,
  FaArrowLeft,
  FaInfoCircle,
  FaBullhorn,
  FaKeyboard,
  FaLanguage,
  FaCheckCircle,
  FaClock,
  FaSlidersH,
  FaLightbulb,
  FaVolumeUp,
  FaUserAlt,
  FaStar,
  FaPhoneAlt,
  FaTags,
  FaEye,
  FaEdit,
  FaLayerGroup,
} from 'react-icons/fa';
import SsmlTextEditor from './SsmlTextEditor';
import { stripSsml, SSML_TAG_TYPES } from './ssmlHelpers';
import './add-tts.scss';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Stepper, useStepper, type StepDescriptor } from '@/components/ui/stepper';
import { Button as ShadStepButton } from '@/components/ui/button';
import { ArrowRight, SlidersHorizontal, Keyboard } from 'lucide-react';

const ADD_TTS_STEPS: StepDescriptor[] = [
  { id: 'setup', label: 'Setup', description: 'Voice & file details', icon: SlidersHorizontal },
  { id: 'script', label: 'Script', description: 'Write, preview, save', icon: Keyboard },
];
import { Button as PageButton } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const SSML_TAG_EXAMPLES: any = {
  currency: {
    example: '₹1,499',
    spoken: 'one thousand four hundred ninety-nine rupees',
  },
  number: { example: '2500', spoken: 'two thousand five hundred' },
  digits: { example: '4708', spoken: 'four seven zero eight' },
  date: { example: '15/07/2026', spoken: 'fifteenth July twenty twenty-six' },
  time: { example: '10:30 am', spoken: 'ten thirty a m' },
  telephone: {
    example: '9876543210',
    spoken: 'nine eight seven six five four three two one zero',
  },
};

/* â”€â”€â”€ constants â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const LANGUAGE_PATTERNS: any = {
  'hi-IN': /^[ऀ-ॿ\s\d.,!?;:'"()\[\]{}-]*$/,
  'eng-IN': /^[A-Za-z0-9\s.,!?;:'"()\[\]{}-]*$/,
  'pi-IN': /^[਀-੿\s\d.,!?;:'"()\[\]{}-]*$/,
};

const LANGUAGE_TONE_OPTIONS = [
  { value: 'teenager', label: 'Teenager', desc: 'Youthful, energetic tone' },
  { value: 'young_adult', label: 'Young Adult', desc: 'Confident, modern tone' },
  { value: 'middle_aged', label: 'Middle-Aged', desc: 'Mature, professional tone' },
  { value: 'elderly', label: 'Elderly', desc: 'Calm, measured tone' },
];

const PITCH_OPTIONS = [
  { value: 'very_low', label: 'Very Low Pitch' },
  { value: 'low', label: 'Low Pitch' },
  { value: 'moderate', label: 'Moderate Pitch' },
  { value: 'high', label: 'High Pitch' },
  { value: 'very_high', label: 'Very High Pitch' },
];

const ACCENT_OPTIONS = [
  { value: 'american', label: 'American Accent' },
  { value: 'british', label: 'British Accent' },
  { value: 'australian', label: 'Australian Accent' },
  { value: 'canadian', label: 'Canadian Accent' },
  { value: 'indian', label: 'Indian Accent' },
  { value: 'chinese', label: 'Chinese Accent' },
];

function isValidText(text: string, lang: string) {
  if (!lang || !text) return true;
  return LANGUAGE_PATTERNS[lang] ? LANGUAGE_PATTERNS[lang].test(text) : true;
}

/* reads tts_operator from the logged-in user's ACL; unset = OMNI VOICE default */
function readTtsOperatorFromUser(user: any) {
  try {
    const ops = user?.acl_settings?.module_operations;
    if (!ops) return DEFAULT_TTS_OPERATOR;
    const parsed = typeof ops === 'string' ? JSON.parse(ops) : ops;
    return normalizeTtsOperatorForSave(parsed?.[MODULE_ACL.TTS]?.tts_operator);
  } catch {
    return DEFAULT_TTS_OPERATOR;
  }
}

/* â”€â”€â”€ Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const AddTTS = () => {
  const navigate = useNavigate();

  const stepper = useStepper(ADD_TTS_STEPS.length);
  const [step0Attempted, setStep0Attempted] = useState(false);

  const [ttsOperator, setTtsOperator] = useState(DEFAULT_TTS_OPERATOR);
  const [language, setLanguage] = useState('');
  const [gender, setGender] = useState('FEMALE');
  const [fileName, setFileName] = useState('');
  const [textScript, setTextScript] = useState('');
  const [categories, setCategories] = useState<any[]>([]);
  const [sendCategory, setSendcategory] = useState('general');
  const [languageTone, setLanguageTone] = useState('middle_aged');
  const [replacedTtsIds, setReplacedTtsIds] = useState<string[]>([]);
  const [pitch, setPitch] = useState('low');
  const [accent, setAccent] = useState('indian');
  const [diffusionSteps, setDiffusionSteps] = useState(32);
  const [speed, setSpeed] = useState(1.0);
  const [userId, setUserId] = useState<any>(null);
  const [ttslanguages, setTtslanguages] = useState<any[]>([]);
  const [ttsFileData, setTtsFileData] = useState<any>(null);
  const [enableTtsOptions, setEnableTtsOptions] = useState(false);
  const [showListenModal, setShowListenModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [promptDuration, setPromptDuration] = useState(0);
  const [callType, setCallType] = useState('');
  const [voiceProfiles, setVoiceProfiles] = useState<any[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [demoQuota, setDemoQuota] = useState<any>(null);
  const [frozenCaptions, setFrozenCaptions] = useState<any[]>([]);
  const [showCaptionsModal, setShowCaptionsModal] = useState(false);
  const [lockedConfig, setLockedConfig] = useState<any>(null);
  const [editingCaptionId, setEditingCaptionId] = useState<any>(null);
  const [pendingMultiItems, setPendingMultiItems] = useState<any[]>([]);
  const [showMultiListenModal, setShowMultiListenModal] = useState(false);

  const isOmni = ttsOperator === 'OMNI_VOICE';
  const selectedProfile =
    voiceProfiles.find((p: any) => String(p.id) === String(selectedProfileId)) ||
    null;
  const isMultiPending = pendingMultiItems.length > 0;

  // Step 0 (Setup) required-field validation.
  // SARVAM: gender, category, file name, call type.
  // OMNI:   voice profile, category, file name.
  // Language + text are entered on step 1 with the SsmlTextEditor.
  const step0Errors: Record<string, string> = {};
  if (!fileName.trim()) step0Errors.fileName = 'File name is required';
  if (!sendCategory) step0Errors.category = 'Category is required';
  if (isOmni) {
    if (!selectedProfileId) step0Errors.voiceProfile = 'Select a voice profile';
  } else {
    if (!gender) step0Errors.gender = 'Select a gender';
    if (!callType) step0Errors.callType = 'Call type is required';
  }
  const step0Valid = Object.keys(step0Errors).length === 0;

  function handleWizardNext() {
    if (stepper.current === 0) {
      if (!step0Valid) {
        setStep0Attempted(true);
        return;
      }
    }
    stepper.next();
  }

  const demoQuotaExhausted =
    !!demoQuota && demoQuota.isDemo && Number(demoQuota.remaining) <= 0;

  const fetchDemoQuota = () => {
    getApiCall(API_TTS_DEMO_QUOTA)
      .then((resp: any) => {
        if (resp && resp.isDemo) setDemoQuota(resp);
        else setDemoQuota(null);
      })
      .catch(() => setDemoQuota(null));
  };

  useEffect(() => {
    const resp = getUser();
    if (resp) {
      setUserId(resp.id);
      setTtsOperator(readTtsOperatorFromUser(resp));
    }
    fetchAndSetFlashMessage(
      (msg: any) =>
        msg.module_name === MODULE_ACL.TTS &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.ADD &&
        msg.type === 'Flash Message',
      setAlertMessage,
    );
  }, []);

  useEffect(() => {
    getApiCall('/v2/voice-category').then((r: any) => {
      if (r?.categories) setCategories(r.categories);
    });
    getApiCall('/v2/tts-languages').then((r: any) => {
      if (Array.isArray(r)) setTtslanguages(r);
    });
    fetchDemoQuota();
  }, []);

  useEffect(() => {
    if (isOmni) {
      getApiCall(API_VOICE_PROFILE).then((r: any) => {
        setVoiceProfiles(Array.isArray(r) ? r : []);
      });
    }
  }, [isOmni]);

  const effectiveLang = language;

  const handleTextScriptChange = (val: string) => {
    if (!effectiveLang) return;
    if (isValidText(stripSsml(val), effectiveLang)) setTextScript(val);
  };

  useEffect(() => {
    if (
      effectiveLang &&
      textScript &&
      !isValidText(stripSsml(textScript), effectiveLang)
    )
      setTextScript('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLang]);

  /* readiness score */
  const readiness = useMemo(() => {
    const checks = isOmni
      ? [!!selectedProfileId, !!fileName.trim(), !!textScript.trim(), !!callType]
      : [!!language, !!gender, !!sendCategory, !!fileName.trim(), !!textScript.trim()];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [
    selectedProfileId,
    language,
    gender,
    sendCategory,
    fileName,
    textScript,
    callType,
    isOmni,
  ]);

  const selectedLanguageLabel =
    ttslanguages.find((l: any) => l.code === language)?.language || '';

  const selectedToneLabel =
    LANGUAGE_TONE_OPTIONS.find((t) => t.value === languageTone)?.label || '';

  const selectedPitchLabel =
    PITCH_OPTIONS.find((p) => p.value === pitch)?.label || '';

  const selectedAccentLabel =
    ACCENT_OPTIONS.find((a) => a.value === accent)?.label || '';

  const completedSteps = useMemo(() => {
    const step1 = isOmni
      ? !!selectedProfileId && !!callType
      : !!language && !!gender && !!sendCategory;
    const step2 = !!fileName.trim() && !!textScript.trim();
    const step3 = enableTtsOptions;
    const step4 = false;
    return [step1, step2, step3, step4];
  }, [
    selectedProfileId,
    callType,
    language,
    gender,
    sendCategory,
    fileName,
    textScript,
    enableTtsOptions,
    isOmni,
  ]);

  const mergeCaption = (
    list: any[],
    lang: string,
    text: string,
    profileId: any,
    profileSnapshot: any,
    editId: any = null,
  ) => {
    const label = ttslanguages.find((l: any) => l.code === lang)?.language || lang;

    if (editId) {
      const idx = list.findIndex((c: any) => c.id === editId);
      if (idx !== -1) {
        const updated = [...list];
        updated[idx] = {
          ...updated[idx],
          language: lang,
          text,
          languageLabel: label,
          profileId,
          profile: profileSnapshot,
        };
        return updated;
      }
    }

    const newId = `${lang}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    return [
      ...list,
      {
        id: newId,
        language: lang,
        languageLabel: label,
        text,
        profileId,
        profile: profileSnapshot,
      },
    ];
  };

  const handleFreezeCaption = () => {
    if (!language) {
      toast.warning('Please select a language first');
      return;
    }
    if (!textScript?.trim()) {
      toast.warning('Please write the caption before freezing');
      return;
    }
    if (isOmni && !selectedProfileId) {
      toast.warning('Please select a voice profile for this language');
      return;
    }
    const label =
      ttslanguages.find((l: any) => l.code === language)?.language || language;

    if (frozenCaptions.length === 0 && !editingCaptionId) {
      setLockedConfig({ callType, fileName });
    }

    setFrozenCaptions((prev: any[]) =>
      mergeCaption(
        prev,
        language,
        textScript,
        isOmni ? selectedProfileId : null,
        isOmni ? selectedProfile : null,
        editingCaptionId,
      ),
    );
    toast.success(
      editingCaptionId
        ? `Caption updated for ${label}`
        : `Caption frozen for ${label}`,
    );
    setLanguage('');
    setTextScript('');
    setEditingCaptionId(null);
  };

  const handleEditCaption = (entry: any) => {
    setLanguage(entry.language);
    setTextScript(entry.text);
    if (isOmni && entry.profileId) {
      setSelectedProfileId(entry.profileId);
    }
    setEditingCaptionId(entry.id);
    setShowCaptionsModal(false);
  };

  const handleDeleteCaption = (id: any) => {
    setFrozenCaptions((prev: any[]) => {
      const updated = prev.filter((c: any) => c.id !== id);
      if (updated.length === 0) setLockedConfig(null);
      return updated;
    });
    setEditingCaptionId((prev: any) => (prev === id ? null : prev));
  };

  const guardedSetCallType = (val: string) => {
    if (lockedConfig && val !== lockedConfig.callType) {
      toast.error(
        'Call type is locked for this batch. Delete all frozen captions to change it.',
      );
      return;
    }
    setCallType(val);
  };

  const guardedSetFileName = (val: string) => {
    if (lockedConfig && val !== lockedConfig.fileName) {
      toast.error(
        'File name is locked for this batch. Delete all frozen captions to change it.',
      );
      return;
    }
    setFileName(val);
  };

  const buildPayload = (entry: any, promptNameOverride: string, regenerateOf: string[] = []) => {
    const profileForEntry = entry.profile || selectedProfile;
    return isOmni
      ? {
          userId,
          regenerateOf,
          gender: profileForEntry.gender.toUpperCase(),
          ttsText: entry.text,
          lang: entry.language,
          promptName: promptNameOverride,
          promptCategory: 'general',
          instruct: [
            profileForEntry.gender,
            profileForEntry.language_tone,
            profileForEntry.pitch,
            profileForEntry.accent,
          ].join(', '),
          speed: profileForEntry.speed,
          operateOn: 'omni',
        }
      : {
          userId,
          regenerateOf,
          gender,
          ttsText: entry.text,
          lang: entry.language,
          promptName: promptNameOverride,
          promptCategory: 'general',
          instruct: 'test',
          speed,
          operateOn: 'sarvam',
        };
  };

  const handleSubmit = async () => {
    setEnableTtsOptions(false);
    if (!userId) {
      toast.warning('User id Required');
      return;
    }
    if (demoQuotaExhausted) {
      toast.error(
        `Daily TTS limit reached for your demo account (${demoQuota.limit} per day). Try again tomorrow.`,
      );
      return;
    }

    let allCaptions =
      language && textScript?.trim()
        ? mergeCaption(
            frozenCaptions,
            language,
            textScript,
            isOmni ? selectedProfileId : null,
            isOmni ? selectedProfile : null,
            editingCaptionId,
          )
        : frozenCaptions;

    if (allCaptions.length === 0) {
      toast.warning('Please select a language and write a caption first.');
      return;
    }

    if (isOmni) {
      if (!fileName?.trim() || !callType) {
        toast.warning('Please fill file name and call type.');
        return;
      }
      const missingProfile = allCaptions.find((c: any) => !c.profile);
      if (missingProfile) {
        toast.warning(
          `Please select a voice profile for ${missingProfile.languageLabel}.`,
        );
        return;
      }
    } else {
      if (!fileName?.trim() || !gender || !sendCategory) {
        toast.warning('Please fill file name, gender, and category.');
        return;
      }
    }

    setSubmitting(true);
    const priorIds = (
      isMultiPending
        ? pendingMultiItems.map((i: any) => i.ttsId)
        : [ttsFileData?.ttsId || ttsFileData?.tts_id || ttsFileData?.id]
    ).filter(Boolean);
    const replacedIds = [...new Set([...replacedTtsIds, ...priorIds].map(String))];
    if (priorIds.length > 0) {
      setReplacedTtsIds(replacedIds);
      setTtsFileData(null);
      setPendingMultiItems([]);
    }

    if (allCaptions.length === 1) {
      const only = allCaptions[0];
      try {
        const response = await fetch(await getTtsConversionUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildPayload(only, fileName.trim(), replacedIds)),
        });
        const data = await response.json();
        const ok = response.ok && data?.status === 'success';

        if (ok) {
          setLanguage(only.language);
          setTextScript(only.text);
          setTtsFileData(data);
          setShowListenModal(true);
          setEnableTtsOptions(true);
          setFrozenCaptions([]);
          setEditingCaptionId(null);
        } else {
          console.error('[TTS single] Generate failed:', data);
          toast.error(friendlyApiError(data?.message || data?.msg));
        }
      } catch (error) {
        console.error('[TTS single] Exception:', error);
        toast.warning('Failed to generate TTS. Please try again.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const generatedItems: any[] = [];
    const failedDetails: any[] = [];

    for (const entry of allCaptions) {
      const promptName = `${fileName.trim()}_${entry.language}`;
      const label = entry.languageLabel || entry.language;
      try {
        const response = await fetch(await getTtsConversionUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildPayload(entry, promptName, replacedIds)),
        });
        const data = await response.json();
        const ok = response.ok && data?.status === 'success';

        if (!ok) {
          const reason = data?.msg || data?.message || `HTTP ${response.status}`;
          console.error(
            `[TTS batch] Generate failed for ${label} (${entry.language}):`,
            reason,
            data,
          );
          failedDetails.push({ label, reason });
          continue;
        }

        const ttsId = data?.ttsId || data?.tts_id || data?.id;
        if (!ttsId) {
          console.error(
            `[TTS batch] No ttsId returned for ${label} (${entry.language}):`,
            data,
          );
          failedDetails.push({
            label,
            reason: 'No TTS id returned by conversion service',
          });
          continue;
        }

        generatedItems.push({
          ttsId,
          language: entry.language,
          text: entry.text,
          languageLabel: label,
          downloadUrl: data?.downloadUrl,
          profileId: entry.profileId,
        });
      } catch (err: any) {
        console.error(
          `[TTS batch] Exception while generating ${label} (${entry.language}):`,
          err,
        );
        failedDetails.push({
          label,
          reason: err?.message || 'Network or parsing error',
        });
      }
    }

    setSubmitting(false);

    if (generatedItems.length > 0) {
      setPendingMultiItems(generatedItems);
      setEnableTtsOptions(true);
      setShowMultiListenModal(true);
      toast.success(
        `${generatedItems.length} of ${allCaptions.length} caption(s) generated. Listen and click Save to finalize.`,
      );
    }

    if (failedDetails.length > 0) {
      failedDetails.forEach((f: any) => toast.error(`${f.label}: ${f.reason}`));
    }
  };

  const resetForm = () => {
    setLanguage('');
    setFileName('');
    setTextScript('');
    setSendcategory('general');
    setTtsFileData(null);
    setEnableTtsOptions(false);
    setPromptDuration(0);
    setCallType('');
    setGender('FEMALE');
    setLanguageTone('middle_aged');
    setPitch('low');
    setAccent('indian');
    setDiffusionSteps(32);
    setSpeed(1.0);
    setSelectedProfileId('');
    setFrozenCaptions([]);
    setLockedConfig(null);
    setPendingMultiItems([]);
    setEditingCaptionId(null);
    setReplacedTtsIds([]);
  };

  /* â”€â”€ Save â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
  const handleSaveTts = async () => {
    if (demoQuotaExhausted) {
      toast.error(
        `Daily TTS limit reached for your demo account (${demoQuota.limit} per day). Try again tomorrow.`,
      );
      return;
    }

    if (isMultiPending) {
      try {
        await postApiCall(API_TTS_MULTI_LANGUAGE, {
          primaryPromptName: fileName.trim(),
          category: sendCategory.toLowerCase(),
          callType,
          voiceProfileId: pendingMultiItems[0]?.profileId || null,
          items: pendingMultiItems.map(
            ({ ttsId, language, text, profileId }: any) => ({
              ttsId,
              language,
              text,
              profileId,
            }),
          ),
          supersedeTtsIds: replacedTtsIds,
        });
        toast.success(`${pendingMultiItems.length} caption(s) saved successfully!`);
        fetchDemoQuota();
        setShowMultiListenModal(false);
        setTimeout(() => navigate('/' + ROUTES.TTS.LIST), 1500);
      } catch (err: any) {
        toast.warning(friendlyApiError(err));
        fetchDemoQuota();
      }
      return;
    }

    const ttsId = ttsFileData?.ttsId || ttsFileData?.tts_id || ttsFileData?.id;
    if (!ttsId) {
      toast.warning('TTS ID not found. Please generate again.');
      return;
    }
    try {
      await postApiCall(API_TTS_CHANGE_STATUS, {
        id: ttsId,
        status: 1,
        promptName: fileName.trim(),
        downloadUrl: ttsFileData?.downloadUrl,
        characterCount: stripSsml(textScript).length,
        category: sendCategory.toLowerCase(),
        promptDuration: String(promptDuration),
        callType,
        voiceProfileId: selectedProfileId || null,
        supersedeTtsIds: replacedTtsIds,
      });
      toast.success('TTS Saved Successfully!');
      fetchDemoQuota();
      setTimeout(() => navigate('/' + ROUTES.TTS.LIST), 2000);
    } catch (err: any) {
      const msg = typeof err === 'string' ? err : err?.message;
      toast.warning(msg || 'Failed to save TTS. Please try again.');
      fetchDemoQuota();
    }
  };

  /* â”€â”€ Discard â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
  const discardReplaced = () =>
    Promise.all(
      replacedTtsIds.map((id) =>
        postApiCall(API_TTS_CHANGE_STATUS, { id, status: 2 }).catch((e: any) => {
          console.error(`[TTS] Discard failed for replaced ttsId ${id}:`, e);
        }),
      ),
    );

  const handleDiscardTts = async () => {
    if (isMultiPending) {
      try {
        await discardReplaced();
        await Promise.all(
          pendingMultiItems.map((item: any) =>
            postApiCall(API_TTS_CHANGE_STATUS, {
              id: item.ttsId,
              status: 2,
            }).catch((e: any) => {
              console.error(
                `[TTS batch] Discard failed for ${item.languageLabel}:`,
                e,
              );
            }),
          ),
        );
        toast.success('TTS captions discarded.');
      } catch {
        toast.warning('Some captions may not have been discarded cleanly.');
      } finally {
        setShowMultiListenModal(false);
        resetForm();
      }
      return;
    }

    const ttsId = ttsFileData?.ttsId || ttsFileData?.tts_id || ttsFileData?.id;
    if (!ttsId) {
      await discardReplaced();
      resetForm();
      return;
    }
    try {
      await discardReplaced();
      await postApiCall(API_TTS_CHANGE_STATUS, { id: ttsId, status: 2 });
      toast.success('TTS Discarded Successfully!');
    } catch {
      toast.warning('Failed to discard TTS. Please try again.');
    } finally {
      resetForm();
    }
  };

  const handleListenClick = () => {
    if (isMultiPending) {
      setShowMultiListenModal(true);
    } else {
      setShowListenModal(true);
    }
  };

  /* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
  if (getUser()?.role !== USER_ROLES.CALL_CENTRE_ADMIN) {
    return <AclDenied operation="add" />;
  }

  return (
    <ScrollPage>
      <PageHeader
        title="Add TTS Prompt"
        description="Compose a text-to-speech prompt with SSML markup, voice selection, and preview."
        actions={
          <PageButton asChild variant="outline">
            <Link to={`/${ROUTES.TTS.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </PageButton>
        }
      />
      <div className="tts-page">

      {/* Demo account daily TTS quota */}
      {demoQuota && demoQuota.isDemo && (
        <div
          className={
            'mb-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ' +
            (demoQuotaExhausted
              ? 'border-destructive/40 bg-destructive/10 text-destructive'
              : 'border-border/60 bg-muted/40 text-foreground')
          }
        >
          <FaInfoCircle className="shrink-0" />
          <span>
            Demo account daily TTS limit:{' '}
            <strong className="tabular-nums">{demoQuota.limit}</strong> · Used today:{' '}
            <strong className="tabular-nums">{demoQuota.used}</strong> · Remaining:{' '}
            <strong className="tabular-nums">{demoQuota.remaining}</strong>
            {demoQuotaExhausted && ' — limit reached, try again tomorrow.'}
          </span>
        </div>
      )}

      {/* Stats strip */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          {
            label: 'Languages',
            value: ttslanguages.length || '—',
            icon: <FaLanguage className="h-4 w-4" />,
          },
          {
            label: 'Voice Types',
            value: 'Male & Female',
            icon: <FaVenusMars className="h-4 w-4" />,
          },
          {
            label: 'Categories',
            value: categories.length || '—',
            icon: <FaFolder className="h-4 w-4" />,
          },
          {
            label: 'TTS Operator',
            value: isOmni ? 'EXPRESS INTERNAL' : 'SARVAM',
            icon: isOmni ? <FaStar className="h-4 w-4" /> : <FaClock className="h-4 w-4" />,
          },
        ].map((s) => (
          <div
            key={s.label}
            className="flex items-center gap-3 rounded-lg border border-border/60 bg-card p-3 shadow-neu-raised"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              {s.icon}
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {s.label}
              </div>
              <div className="truncate text-sm font-bold text-foreground">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Main content grid */}
      <div className="tts-top-refs">
        <div className="tts-top-refs__card tts-top-refs__card--workflow">
            {/* Workflow steps */}
            <div className="tts-sidebar-card">
              <div className="tts-sidebar-card__header">
                <div
                  className="tts-sidebar-card__header-icon text-primary"
                >
                  <FaCheckCircle />
                </div>
                <h6>How it works</h6>
              </div>
              <div className="tts-sidebar-card__body">
                <div className="tts-steps">
                  {[
                    {
                      label: 'Configure',
                      desc: isOmni
                        ? 'Choose language, gender, category and tone.'
                        : 'Choose language, gender and category.',
                    },
                    {
                      label: 'Write Script',
                      desc: 'Enter the text you want converted to speech.',
                    },
                    {
                      label: 'Generate',
                      desc: 'Click Generate TTS and wait a few seconds.',
                    },
                    {
                      label: 'Review & Save',
                      desc: 'Preview the audio then Save or Discard.',
                    },
                  ].map((s: any, i: number) => {
                    const done = completedSteps[i];
                    return (
                      <div
                        className={`tts-step${done ? ' tts-step--done' : ''}`}
                        key={i}
                      >
                        <div
                          className={
                            'tts-step__num ' +
                            (done ? 'text-success ring-1 ring-success/40' : 'text-primary')
                          }
                        >
                          {done ? (
                            <FaCheckCircle style={{ fontSize: '0.8rem' }} />
                          ) : (
                            i + 1
                          )}
                        </div>
                        <div className="tts-step__body">
                          <div
                            className={'tts-step__title ' + (done ? 'text-success' : '')}
                          >
                            {s.label}
                          </div>
                          <div className="tts-step__desc">{s.desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
        </div>
        <div className="tts-top-refs__card tts-top-refs__card--spoken">
            {/* Spoken-as Types */}
            <div className="tts-sidebar-card">
              <div className="tts-sidebar-card__header">
                <div
                  className="tts-sidebar-card__header-icon text-primary"
                >
                  <FaTags />
                </div>
                <h6>Spoken-as Types</h6>
              </div>
              <div className="tts-sidebar-card__body">
                <div className="tts-spoken-types">
                  {SSML_TAG_TYPES.map((t: any) => {
                    const ex = SSML_TAG_EXAMPLES[t.interpretAs];
                    return (
                      <div className="tts-spoken-type" key={t.key}>
                        <div className="tts-spoken-type__head">
                          <span className="tts-spoken-type__glyph">
                            {t.glyph}
                          </span>
                          <span className="tts-spoken-type__label">
                            {t.label}
                          </span>
                          <code className="tts-spoken-type__code">
                            interpret-as="{t.interpretAs}"
                          </code>
                        </div>
                        {ex && (
                          <div className="tts-spoken-type__example">
                            <strong>{ex.example}</strong> → "{ex.spoken}"
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
        </div>
      </div>

      <div className="tts-content">
        {/* â”€â”€ Form card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="tts-form-card">
          <div className="mb-4 flex items-center justify-between gap-3 border-b border-border/60 pb-3">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                <FaSlidersH className="h-4 w-4" />
              </span>
              <div>
                <div className="text-sm font-semibold text-foreground">
                  Audio Configuration
                </div>
                <div className="text-xs text-muted-foreground">
                  {isOmni
                    ? 'Configure voice, tone and script below.'
                    : 'Configure language, gender and script below.'}
                </div>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
              {isOmni ? <FaStar /> : <FaMicrophone />}
              {isOmni ? 'EXPRESS INTERNAL' : 'SARVAM'}
            </span>
          </div>

          <div className="tts-form-card__body">
            <Stepper
              steps={ADD_TTS_STEPS}
              current={stepper.current}
              completed={stepper.completed}
              onStepClick={stepper.goTo}
              className="mb-6"
            />

            {/* Step 0: Setup */}
            {stepper.current === 0 && (
            <>
            {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
                SARVAM FORM
            â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
            {!isOmni && (
              <>
                {/* Voice Settings */}
                <div className="tts-section">
                  <div className="tts-section-title">
                    <FaVolumeUp /> Voice Settings
                  </div>

                  {/* Gender tiles */}
                  <div className="tts-field-group" style={{ marginBottom: '1.1rem' }}>
                    <label className="tts-field-label">
                      <FaVenusMars /> Voice Gender <span className="req">*</span>
                    </label>
                    <div className="tts-gender-row">
                      <div
                        className={`tts-gender-tile tts-gender-tile--male ${
                          gender === 'MALE' ? 'active' : ''
                        }`}
                        onClick={() => setGender('MALE')}
                      >
                        <div className="tts-gender-tile__icon">
                          <FaMale />
                        </div>
                        <span className="tts-gender-tile__name">Male</span>
                        <span className="tts-gender-tile__desc">
                          Deep &amp; clear voice
                        </span>
                      </div>
                      <div
                        className={`tts-gender-tile tts-gender-tile--female ${
                          gender === 'FEMALE' ? 'active' : ''
                        }`}
                        onClick={() => setGender('FEMALE')}
                      >
                        <div className="tts-gender-tile__icon">
                          <FaFemale />
                        </div>
                        <span className="tts-gender-tile__name">Female</span>
                        <span className="tts-gender-tile__desc">
                          Warm &amp; natural voice
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Language */}
                  <div className="tts-field-row">
                    <div className="tts-field-group">
                      <label className="tts-field-label">
                        <FaGlobe /> Language <span className="req">*</span>
                      </label>
                      <select
                        className="tts-select"
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                      >
                        <option value="">Choose language…</option>
                        {ttslanguages.map((item: any) => (
                          <option key={item.code} value={item.code}>
                            {item.language}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
                EXPRESS INTERNAL FORM
            â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
            {isOmni && (
              <>
                <div className="tts-section">
                  <div className="tts-section-title">
                    <FaVolumeUp /> Voice Settings
                  </div>

                  <div className="tts-field-row">
                    {/* Voice Profile dropdown — NOT locked, can differ per language */}
                    <div className="tts-field-group">
                      <label className="tts-field-label">
                        <FaUserAlt /> Voice Profile <span className="req">*</span>
                      </label>
                      {voiceProfiles.length === 0 ? (
                        <div
                          style={{
                            padding: '0.6rem 0.9rem',
                            background: '#fef3c7',
                            borderRadius: 8,
                            fontSize: '0.82rem',
                            color: '#92400e',
                            border: '1px solid #fde68a',
                          }}
                        >
                          No voice profiles found.{' '}
                          <span
                            style={{
                              color: '#7c3aed',
                              cursor: 'pointer',
                              fontWeight: 700,
                              textDecoration: 'underline',
                            }}
                            onClick={() =>
                              navigate('/' + ROUTES.TTS.VOICE_PROFILE_LIST)
                            }
                          >
                            Create one
                          </span>
                        </div>
                      ) : (
                        <select
                          className="tts-select"
                          value={selectedProfileId}
                          onChange={(e) => setSelectedProfileId(e.target.value)}
                        >
                          <option value="">Select voice profile…</option>
                          {voiceProfiles.map((p: any) => (
                            <option key={p.id} value={p.id}>
                              {p.flow_name || `Profile #${p.id}`} — {p.gender} |{' '}
                              {p.language} | {p.category}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* Call Type — locked once first caption is frozen */}
                    <div className="tts-field-group">
                      <label className="tts-field-label">
                        <FaPhoneAlt /> Call Type <span className="req">*</span>
                      </label>
                      <select
                        className="tts-select"
                        value={callType}
                        onChange={(e) => guardedSetCallType(e.target.value)}
                      >
                        <option value="">Choose call type…</option>
                        <option value="1">OUTBOUND</option>
                        <option value="2">INBOUND</option>
                        <option value="3">BOTH</option>
                      </select>
                    </div>
                  </div>

                  {/* Language */}
                  <div className="tts-field-row" style={{ marginTop: '1rem' }}>
                    <div className="tts-field-group">
                      <label className="tts-field-label">
                        <FaGlobe /> Language <span className="req">*</span>
                      </label>
                      <select
                        className="tts-select"
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                      >
                        <option value="">Choose language…</option>
                        {ttslanguages.map((item: any) => (
                          <option key={item.code} value={item.code}>
                            {item.language}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Selected profile summary */}
                  {selectedProfile && (
                    <div
                      className="tts-tone-selected"
                      style={{ marginTop: '0.9rem' }}
                    >
                      <FaCheckCircle className="text-success" />
                      <span>
                        <strong>{selectedProfile.gender}</strong> &middot;{' '}
                        {selectedProfile.language} &middot; {selectedProfile.category}{' '}
                        &middot; {selectedProfile.language_tone} &middot;{' '}
                        {selectedProfile.pitch} &middot; {selectedProfile.accent}{' '}
                        &middot; {Number(selectedProfile.speed).toFixed(2)}x
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* â”€â”€ File Details (shared) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <div className="tts-section">
              <div className="tts-section-title">
                <FaFileAlt /> File Details
              </div>
              {isOmni ? (
                <div className="tts-field-group">
                  <label className="tts-field-label">
                    <FaFileAlt /> File Name <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    className={
                      'tts-input' +
                      (step0Attempted && step0Errors.fileName ? ' is-invalid' : '')
                    }
                    placeholder="e.g. welcome_message, greeting_ivr"
                    value={fileName}
                    onChange={(e) => guardedSetFileName(e.target.value)}
                  />
                  {step0Attempted && step0Errors.fileName && (
                    <div className="tts-field-error">{step0Errors.fileName}</div>
                  )}
                </div>
              ) : (
                <div className="tts-field-row">
                  <div className="tts-field-group">
                    <label className="tts-field-label">
                      <FaFileAlt /> File Name <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      className={
                        'tts-input' +
                        (step0Attempted && step0Errors.fileName ? ' is-invalid' : '')
                      }
                      placeholder="e.g. welcome_message, greeting_ivr"
                      value={fileName}
                      onChange={(e) => guardedSetFileName(e.target.value)}
                    />
                    {step0Attempted && step0Errors.fileName && (
                      <div className="tts-field-error">{step0Errors.fileName}</div>
                    )}
                  </div>
                  <div className="tts-field-group">
                    <label className="tts-field-label">
                      <FaPhoneAlt /> Call Type <span className="req">*</span>
                    </label>
                    <select
                      className={
                        'tts-select' +
                        (step0Attempted && step0Errors.callType ? ' is-invalid' : '')
                      }
                      value={callType}
                      onChange={(e) => guardedSetCallType(e.target.value)}
                    >
                      <option value="">Choose call type…</option>
                      <option value="1">OUTBOUND</option>
                      <option value="2">INBOUND</option>
                      <option value="3">BOTH</option>
                    </select>
                    {step0Attempted && step0Errors.callType && (
                      <div className="tts-field-error">{step0Errors.callType}</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            </>
            )}

            {/* Step 1: Script */}
            {stepper.current === 1 && (
            <>

            {/* â”€â”€ Text Script (shared) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <div className="tts-section" style={{ marginBottom: 0 }}>
              <div className="tts-section-title">
                <FaKeyboard /> Text Script
              </div>

              <div className="tts-field-group">
                <label className="tts-field-label">
                  <FaKeyboard /> Your Text <span className="req">*</span>
                </label>
                <SsmlTextEditor
                  className="tts-textarea"
                  value={textScript}
                  onChange={handleTextScriptChange}
                  lang={language ? language.split('-')[0] : 'en'}
                  rate={isOmni ? selectedProfile?.speed || 1.0 : speed}
                  disabled={!effectiveLang}
                  placeholder={
                    !effectiveLang
                      ? isOmni
                        ? 'Select a voice profile above to unlock text entry…'
                        : 'Select a language above to unlock text entry…'
                      : `Type your script here${
                          selectedLanguageLabel || selectedProfile?.language
                            ? ` in ${selectedLanguageLabel || selectedProfile?.language}`
                            : ''
                        }…`
                  }
                />
                {!effectiveLang && (
                  <div className="tts-textarea-footer">
                    <span className="tts-lang-hint">
                      <FaInfoCircle />{' '}
                      {isOmni
                        ? 'Select a voice profile to enable input'
                        : 'Select a language to enable input'}
                    </span>
                  </div>
                )}

                {editingCaptionId && (
                  <div className="tts-lang-hint" style={{ marginTop: '6px' }}>
                    <FaInfoCircle /> Editing an existing frozen caption — freezing
                    will update just this one.
                    <button
                      type="button"
                      onClick={() => {
                        setEditingCaptionId(null);
                        setLanguage('');
                        setTextScript('');
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        marginLeft: '6px',
                        color: 'hsl(var(--primary))',
                        fontWeight: 600,
                        fontSize: '0.76rem',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Cancel edit
                    </button>
                  </div>
                )}
                <div className="tts-caption-actions">
                  <button
                    type="button"
                    className="tts-btn tts-btn--freeze"
                    onClick={handleFreezeCaption}
                    disabled={!language || !textScript?.trim()}
                  >
                    <FaSave />{' '}
                    {editingCaptionId ? 'Update Caption' : 'Freeze Caption'}
                  </button>
                  <button
                    type="button"
                    className="tts-btn tts-btn--view-captions"
                    onClick={() => setShowCaptionsModal(true)}
                    disabled={frozenCaptions.length === 0}
                  >
                    <FaEye /> View Captions
                    {frozenCaptions.length > 0 ? ` (${frozenCaptions.length})` : ''}
                  </button>
                </div>
              </div>

            </div>

            <div className="tts-divider" />

            {/* Action bar */}
            <div className="tts-action-bar">
              <button
                type="button"
                className={`tts-btn tts-btn--generate${
                  enableTtsOptions ? ' tts-btn--generated' : ''
                }`}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="tts-btn-spinner" /> Generating…
                  </>
                ) : enableTtsOptions ? (
                  <>
                    <FaCheckCircle /> Re-Generate
                  </>
                ) : (
                  <>
                    <FaPaperPlane /> Generate TTS
                  </>
                )}
              </button>

              <button
                type="button"
                className={`tts-btn tts-btn--listen${
                  enableTtsOptions ? ' tts-btn--active-glow' : ''
                }`}
                disabled={!enableTtsOptions}
                onClick={handleListenClick}
              >
                <FaHeadphones /> Listen
                {isMultiPending ? ` (${pendingMultiItems.length})` : ''}
              </button>

              <button
                type="button"
                className={`tts-btn tts-btn--save${
                  enableTtsOptions ? ' tts-btn--active-glow' : ''
                }`}
                disabled={!enableTtsOptions}
                onClick={handleSaveTts}
              >
                <FaSave /> Save
              </button>

              <button
                type="button"
                className={`tts-btn tts-btn--discard${
                  enableTtsOptions ? ' tts-btn--active-glow' : ''
                }`}
                disabled={!enableTtsOptions}
                onClick={handleDiscardTts}
              >
                <FaTrashAlt /> Discard
              </button>

              <button
                type="button"
                className="tts-btn tts-btn--back"
                onClick={() => navigate(-1)}
              >
                <FaArrowLeft /> Back to List
              </button>
            </div>

            <div className="tts-note">
              <FaInfoCircle />
              After clicking <strong>Generate TTS</strong>, listen to the preview
              audio. Use <strong>Save</strong> to store it or{' '}
              <strong>Discard</strong> to remove it.
            </div>
            </>
            )}

            {/* Wizard nav */}
            <div className="mt-6 flex items-center justify-between border-t border-border/60 pt-4">
              <ShadStepButton
                type="button"
                variant="ghost"
                onClick={stepper.prev}
                disabled={stepper.current === 0}
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </ShadStepButton>
              {stepper.current < ADD_TTS_STEPS.length - 1 && (
                <ShadStepButton type="button" onClick={handleWizardNext}>
                  Next
                  <ArrowRight className="h-4 w-4" />
                </ShadStepButton>
              )}
            </div>
          </div>
        </div>

        <div className="tts-sidebar">
          {/* Configuration summary */}
          <div className="tts-sidebar-card">
            <div className="tts-sidebar-card__header">
              <div
                className="tts-sidebar-card__header-icon text-primary"
              >
                <FaSlidersH />
              </div>
              <h6>Configuration Summary</h6>
            </div>
            <div className="tts-sidebar-card__body">
              <div className="tts-summary-list">
                {[
                  {
                    icon: <FaMicrophone />,
                    label: 'Operator',
                    val: isOmni ? 'EXPRESS INTERNAL' : 'SARVAM',
                  },
                  ...(isOmni
                    ? [
                        {
                          icon: <FaUserAlt />,
                          label: 'Gender',
                          val: selectedProfile?.gender || '',
                        },
                        {
                          icon: <FaGlobe />,
                          label: 'Language',
                          val: selectedProfile?.language || '',
                        },
                        {
                          icon: <FaFolder />,
                          label: 'Category',
                          val: selectedProfile?.category || '',
                        },
                        {
                          icon: <FaUserAlt />,
                          label: 'Tone',
                          val: selectedProfile?.language_tone || '',
                        },
                        {
                          icon: <FaSlidersH />,
                          label: 'Pitch',
                          val: selectedProfile?.pitch || '',
                        },
                        {
                          icon: <FaGlobe />,
                          label: 'Accent',
                          val: selectedProfile?.accent || '',
                        },
                        {
                          icon: <FaVolumeUp />,
                          label: 'Speed',
                          val: selectedProfile
                            ? `${Number(selectedProfile.speed).toFixed(2)}x`
                            : '',
                        },
                      ]
                    : [
                        {
                          icon: <FaGlobe />,
                          label: 'Language',
                          val: selectedLanguageLabel,
                        },
                        {
                          icon: <FaVenusMars />,
                          label: 'Gender',
                          val: gender
                            ? gender === 'MALE'
                              ? 'Male'
                              : 'Female'
                            : '',
                        },
                      ]),
                  {
                    icon: <FaFileAlt />,
                    label: 'File Name',
                    val: fileName.trim(),
                  },
                  {
                    icon: <FaPhoneAlt />,
                    label: 'Call Type',
                    val:
                      callType === '1'
                        ? 'OUTBOUND'
                        : callType === '2'
                        ? 'INBOUND'
                        : callType === '3'
                        ? 'BOTH'
                        : '',
                  },
                  {
                    icon: <FaKeyboard />,
                    label: 'Characters',
                    val: stripSsml(textScript).length
                      ? `${stripSsml(textScript).length} chars`
                      : '',
                  },
                ].map((r: any, i: number) => (
                  <React.Fragment key={i}>
                    {i > 0 && <div className="tts-summary-divider" />}
                    <div className="tts-summary-row">
                      <span className="tts-summary-row__label">
                        {r.icon} {r.label}
                      </span>
                      <span
                        className={`tts-summary-row__val${
                          r.val ? '' : ' tts-summary-row__val--empty'
                        }`}
                      >
                        {r.val || 'Not set'}
                      </span>
                    </div>
                  </React.Fragment>
                ))}
              </div>

              <div className="tts-readiness-bar">
                <div className="tts-readiness-bar__top">
                  <span>Readiness</span>
                  <span style={{ color: readiness === 100 ? 'hsl(var(--success))' : 'hsl(var(--muted-foreground))' }}>
                    {readiness}%
                  </span>
                </div>
                <div className="tts-readiness-bar__track">
                  <div
                    className="tts-readiness-bar__fill"
                    style={{ width: `${readiness}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="tts-sidebar-card">
            <div className="tts-sidebar-card__header">
              <div
                className="tts-sidebar-card__header-icon"
                style={{ color: '#ca8a04' }}
              >
                <FaLightbulb />
              </div>
              <h6>Tips for best results</h6>
            </div>
            <div className="tts-sidebar-card__body">
              <div className="tts-tips">
                {(isOmni
                  ? [
                      {
                        dot: 'hsl(var(--primary))',
                        text: 'Select the tone that matches your audience — elderly for formal IVRs, young adult for modern apps.',
                      },
                      {
                        dot: 'hsl(var(--primary))',
                        text: 'Use short, clear sentences for the most natural output.',
                      },
                      {
                        dot: 'hsl(var(--success))',
                        text: 'Match the script language to the selected language setting.',
                      },
                      {
                        dot: 'hsl(var(--warning))',
                        text: "Avoid abbreviations; write out full words (e.g. 'Doctor' not 'Dr').",
                      },
                      {
                        dot: 'hsl(var(--destructive))',
                        text: 'Numbers should be written as words for better naturalness.',
                      },
                    ]
                  : [
                      {
                        dot: 'hsl(var(--primary))',
                        text: 'Use proper punctuation — commas and periods control natural pauses.',
                      },
                      {
                        dot: 'hsl(var(--primary))',
                        text: 'Keep sentences short (under 20 words) for the clearest pronunciation.',
                      },
                      {
                        dot: 'hsl(var(--success))',
                        text: 'Match the script language to the selected language setting.',
                      },
                      {
                        dot: 'hsl(var(--warning))',
                        text: "Avoid abbreviations; write out full words (e.g. 'Doctor' not 'Dr').",
                      },
                      {
                        dot: 'hsl(var(--destructive))',
                        text: 'Numbers should be written as words for better naturalness.',
                      },
                    ]
                ).map((t: any, i: number) => (
                  <div className="tts-tip" key={i}>
                    <div className="tts-tip__dot" style={{ background: t.dot }} />
                    <span className="tts-tip__text">{t.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Captions modal */}
      <Modal
        show={showCaptionsModal}
        onHide={() => setShowCaptionsModal(false)}
        centered
        className="tts-captions-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaLayerGroup /> Frozen Captions ({frozenCaptions.length})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {frozenCaptions.length === 0 ? (
            <div className="tts-lang-hint">
              <FaInfoCircle /> No captions frozen yet.
            </div>
          ) : (
            <div className="tts-captions-list">
              {frozenCaptions.map((entry: any) => (
                <div className="tts-caption-item" key={entry.id}>
                  <div className="tts-caption-item__head">
                    <span className="tts-caption-item__lang">
                      <FaGlobe /> {entry.languageLabel}
                    </span>
                    {entry.profile && (
                      <span
                        style={{
                          fontSize: '11px',
                          opacity: 0.7,
                          marginLeft: '6px',
                        }}
                      >
                        (
                        {entry.profile.flow_name || `Profile #${entry.profileId}`})
                      </span>
                    )}
                    <div className="tts-caption-item__actions">
                      <button
                        type="button"
                        className="tts-icon-btn"
                        onClick={() => handleEditCaption(entry)}
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        className="tts-icon-btn tts-icon-btn--danger"
                        onClick={() => handleDeleteCaption(entry.id)}
                        title="Delete"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </div>
                  <p className="tts-caption-item__text">{stripSsml(entry.text)}</p>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Listen modal — single caption */}
      <Modal
        show={showListenModal && !!ttsFileData?.downloadUrl}
        onHide={() => setShowListenModal(false)}
        centered
        className="tts-listen-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaHeadphones /> TTS Playback
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="tts-audio-player">
            <div className="tts-audio-label">
              <FaVolumeUp /> Preview your generated audio
            </div>
            <audio
              controls
              style={{ width: '100%' }}
              onLoadedMetadata={(e: any) => setPromptDuration(e.target.duration)}
            >
              <source src={ttsFileData?.downloadUrl} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          </div>
        </Modal.Body>
      </Modal>

      {/* Listen modal — multi-language: one player per generated language, all pending Save */}
      <Modal
        show={showMultiListenModal}
        onHide={() => setShowMultiListenModal(false)}
        centered
        className="tts-listen-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaHeadphones /> TTS Playback ({pendingMultiItems.length} languages)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingMultiItems.map((item: any) => (
            <div
              key={item.ttsId}
              className="tts-audio-player"
              style={{ marginBottom: '14px' }}
            >
              <div className="tts-audio-label">
                <FaGlobe /> {item.languageLabel}
              </div>
              <audio controls style={{ width: '100%' }}>
                <source src={item.downloadUrl} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </div>
          ))}
          <div className="tts-note" style={{ marginTop: '8px' }}>
            <FaInfoCircle />
            Click <strong>Save</strong> below to finalize all{' '}
            {pendingMultiItems.length} languages into one record, or{' '}
            <strong>Discard</strong> to remove them.
          </div>
        </Modal.Body>
      </Modal>
      </div>


    </ScrollPage>
  );
};

export default AddTTS;
